import React from 'react';
import { interpolateColor } from '../utils/colors';
import type {
    CoffeeRateInterface,
    CoffeeMachineRef,
    CoffeeBeanRef,
    CoffeeCapsuleRef,
} from '../types';

interface StatsOverlayProps {
    coffeeData: CoffeeRateInterface;
}

/** A populated ref is an object carrying an `_id`; a bare id string is not. */
const isPopulated = <T extends { _id: string }>(x: unknown): x is T =>
    typeof x === 'object' && x !== null && '_id' in x;

/** Pull a printable scalar out of the free-form brew config under one of the keys. */
const brewValue = (
    brew: Record<string, unknown> | undefined,
    ...keys: string[]
): string | null => {
    if (!brew) return null;
    for (const key of keys) {
        const raw = brew[key];
        if (raw == null) continue;
        const str = typeof raw === 'number' ? String(raw) : String(raw).trim();
        if (str) return str;
    }
    return null;
};

type EquipmentRow = { label: string; value: string };

/**
 * Compose a compact, human-readable summary of the home setup behind a rating
 * (machine + beans/capsule + brew). Every value is conditional, so the chip
 * only ever shows the fields that were actually recorded.
 */
const equipmentRows = (home: NonNullable<CoffeeRateInterface['home']>): EquipmentRow[] => {
    const rows: EquipmentRow[] = [];
    const brew = home.brew as Record<string, unknown> | undefined;

    const machine = home.machineRef;
    if (isPopulated<CoffeeMachineRef>(machine)) {
        const name = [machine.brand, machine.model].filter(Boolean).join(' ').trim();
        const value = machine.type ? `${name} (${machine.type})` : name;
        if (value) rows.push({ label: 'Machine', value });
    }

    const bean = home.beanRef;
    if (isPopulated<CoffeeBeanRef>(bean)) {
        const name = [bean.roaster, bean.name].filter(Boolean).join(' — ').trim();
        if (name) rows.push({ label: 'Beans', value: name });
        if (bean.roastLevel) rows.push({ label: 'Roast', value: String(bean.roastLevel) });
        if (bean.origin) rows.push({ label: 'Origin', value: bean.origin });
        if (bean.process) rows.push({ label: 'Process', value: bean.process });
    }

    const capsule = home.capsuleRef;
    if (isPopulated<CoffeeCapsuleRef>(capsule)) {
        const name = [capsule.brand, capsule.name].filter(Boolean).join(' ').trim();
        if (name) rows.push({ label: 'Capsule', value: name });
        if (capsule.intensity != null) rows.push({ label: 'Intensity', value: String(capsule.intensity) });
    }

    // Brew settings (free-form). Manual brews carry dose/grind/notes; capsule
    // brews carry intensity. Only add what isn't already covered above.
    const dose = brewValue(brew, 'doseGrams', 'dose');
    if (dose) rows.push({ label: 'Dose', value: /g\b/.test(dose) ? dose : `${dose} g` });
    const grind = brewValue(brew, 'grind');
    if (grind) rows.push({ label: 'Grind', value: grind });
    if (!isPopulated<CoffeeCapsuleRef>(capsule)) {
        const intensity = brewValue(brew, 'intensity');
        if (intensity) rows.push({ label: 'Intensity', value: intensity });
    }
    const capsuleType = brewValue(brew, 'capsuleType');
    if (capsuleType) rows.push({ label: 'Capsule type', value: capsuleType });
    const brewNotes = brewValue(brew, 'notes');
    if (brewNotes) rows.push({ label: 'Notes', value: brewNotes });

    return rows;
};

/**
 * Detailed-ratings overlay (full image-section coverage with slide-up).
 * No close button — the action bar's "Hide Stats" toggle (still visible at the
 * bottom-right via z-index 20) is what closes this view.
 */
export const StatsOverlay: React.FC<StatsOverlayProps> = ({ coffeeData }) => {
    const ratingEntries = coffeeData.rating ? Object.entries(coffeeData.rating) : [];

    // Verified-master home ratings can carry the machine / beans / capsule / brew
    // behind the cup. Surface it as an "Equipment" panel styled like the description.
    const equipment =
        coffeeData.kind === 'home' && coffeeData.home
            ? equipmentRows(coffeeData.home)
            : [];

    return (
        <div className='detailed-ratings slide-up' role='dialog' aria-label='Coffee stats'>
            {coffeeData.description && (
                <div className='description-section'>
                    <div className='description-label'>Description:</div>
                    <div className='description-text'>{coffeeData.description}</div>
                </div>
            )}
            {equipment.length > 0 && (
                <div className='equipment-section'>
                    <div className='equipment-label'>Equipment</div>
                    <div className='equipment-list'>
                        {equipment.map((row) => (
                            <div key={row.label} className='equipment-row'>
                                <span className='equipment-row__key'>{row.label}</span>
                                <span className='equipment-row__val'>{row.value}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {ratingEntries.length > 0 ? (
                ratingEntries.map(([key, value]) => {
                    const numeric = parseFloat(value);
                    const color = isNaN(numeric) ? '#666' : interpolateColor(numeric / 10);
                    return (
                        <div key={key} className='rating-item'>
                            <div>{key}</div>
                            <div style={{ color }}>{value}</div>
                        </div>
                    );
                })
            ) : (
                <div className='no-ratings'>No ratings available</div>
            )}
        </div>
    );
};
